import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  CornerDownRightIcon,
  EditIcon,
  EllipsisIcon,
  TrashIcon,
} from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { CommentAuthorHeader } from "./CommentAuthorHeader";
import { ReplyComposer } from "./ReplyComposer";

import { MentionTextInput, RichTextDisplay, Row, Column } from "@/components";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";

import { useDeleteInternalCandidacyComment } from "@/hooks/mutations/useDeleteInternalCandidacyComment";
import { useUpdateInternalCandidacyComment } from "@/hooks/mutations/useUpdateInternalCandidacyComment";
import { useGetCurrentUser } from "@/hooks/queries/useGetCurrentUser";
import { useGetUser } from "@/hooks/queries/useGetUser";
import { useSetInternalCandidacyCommentInInternalCandidacyComments } from "@/hooks/sets/useSetInternalCandidacyCommentInInternalCandidacyComments";
import { useSetRemoveInternalCandidacyCommentInInternalCandidacyComments } from "@/hooks/sets/useSetRemoveInternalCandidacyCommentInInternalCandidacyComments";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { tiptapContentJsonSchema } from "@/lib/schemas";
import { tiptap } from "@/lib/tiptap";
import { cn } from "@/lib/utils";
import type { InternalCandidacyComment as InternalCandidacyCommentType } from "@/types";

const jsonToHtml = tiptap.jsonToHtmlFn();

const updateCommentSchema = z.object({
  contentJson: tiptapContentJsonSchema,
});

const UpdateInternalCommentForm = ({
  comment,
  onClose,
}: {
  comment: InternalCandidacyCommentType;
  onClose: () => void;
}) => {
  /* Database CRUD Operations */
  // Update
  const { isPending, mutate: updateComment } =
    useUpdateInternalCandidacyComment();

  /* Cache Updates */
  // Set
  const setCommentInComments =
    useSetInternalCandidacyCommentInInternalCandidacyComments();

  /* React Hooks */
  const form = useForm<z.infer<typeof updateCommentSchema>>({
    defaultValues: {
      contentJson: comment.contentJson as { type: "doc"; content: unknown[] },
    },
    resolver: zodResolver(updateCommentSchema),
  });

  /* Handlers */
  const handleSubmit = (data: z.infer<typeof updateCommentSchema>) => {
    if (isPending) return;

    mixpanelUtils.trackFormSubmission(
      "Update comment",
      MIXPANEL_SCREENS.CANDIDATE_SHEET,
    );
    updateComment(
      {
        candidacyId: comment.candidacyId,
        internalCandidacyCommentId: comment.id,
        updateInternalCandidacyCommentRequest: {
          contentJson: data.contentJson,
        },
      },
      {
        onSuccess: (updatedComment) => {
          setCommentInComments({
            candidacyId: comment.candidacyId,
            internalCandidacyCommentToUpdate: updatedComment,
          });
          onClose();
        },
      },
    );
  };

  /* Render */
  const render = () => {
    return (
      <Form {...form}>
        <form
          className="flex flex-col gap-2"
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onSubmit={form.handleSubmit(handleSubmit)}
        >
          <FormField
            control={form.control}
            name="contentJson"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <MentionTextInput
                    autoFocus
                    onChange={field.onChange}
                    onSubmit={() => void form.handleSubmit(handleSubmit)()}
                    value={field.value}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Row className="items-center justify-end gap-2">
            <Button
              className="rounded-full"
              onClick={onClose}
              type="button"
              variant="ghost"
            >
              Cancel
            </Button>
            <Button
              className="rounded-full"
              disabled={isPending}
              type="submit"
              variant="outline"
            >
              Update
            </Button>
          </Row>
        </form>
      </Form>
    );
  };

  return render();
};

export const InternalComment = ({
  comment,
  isReply = false,
  onToggleReplying,
  replies = [],
  replyingCommentId,
}: {
  comment: InternalCandidacyCommentType;
  isReply?: boolean;
  onToggleReplying?: (commentId: number | null) => void;
  replies?: InternalCandidacyCommentType[];
  replyingCommentId?: number | null;
}) => {
  /* Database CRUD Operations */
  // Read
  const { data: author, status: authorStatus } = useGetUser({
    userId: comment.userId,
  });
  // Delete
  const { mutate: deleteComment } = useDeleteInternalCandidacyComment();

  /* Cache Updates */
  // Set
  const setRemoveCommentInComments =
    useSetRemoveInternalCandidacyCommentInInternalCandidacyComments();

  /* React Hooks */
  const [isEditing, setIsEditing] = useState(false);
  const currentUser = useGetCurrentUser();

  /* Helpers */
  const hasAuthor = authorStatus === "success" && !!author;
  const authorName = hasAuthor
    ? `${author.firstName} ${author.lastName}`
    : "Staffer";
  const authorInitials = hasAuthor
    ? `${author.firstName[0]}${author.lastName[0]}`
    : "?";
  const hasReplies = replies.length > 0;
  const isOwnComment = currentUser?.id === comment.userId;
  const isReplying = replyingCommentId === comment.id;

  /* Handlers */
  const handleDelete = () => {
    mixpanelUtils.trackButtonClick(
      "Delete Comment",
      MIXPANEL_SCREENS.CANDIDATE_SHEET,
    );
    deleteComment(
      {
        candidacyId: comment.candidacyId,
        internalCandidacyCommentId: comment.id,
      },
      {
        onSuccess: (removedComment) => {
          setRemoveCommentInComments({
            candidacyId: comment.candidacyId,
            internalCandidacyCommentToRemove: removedComment,
          });
        },
      },
    );
  };

  /* Render */
  const renderActionsMenu = () => {
    if (!isOwnComment) return null;

    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button className="shrink-0" size="icon" variant="ghost">
            <EllipsisIcon className="h-4 w-4 text-slate-700" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-[110px] p-0">
          <DropdownMenuItem
            onClick={() => {
              setIsEditing(true);
            }}
          >
            <EditIcon className="h-4 w-4" />
            Edit
          </DropdownMenuItem>
          {/* A thread's first comment anchors its replies — no deleting it while replies remain. */}
          {hasReplies ? null : (
            <DropdownMenuItem className="text-red-500" onClick={handleDelete}>
              <TrashIcon className="h-4 w-4" />
              Remove
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  const renderComment = () => {
    if (isEditing) {
      return (
        <UpdateInternalCommentForm
          comment={comment}
          onClose={() => {
            setIsEditing(false);
          }}
        />
      );
    }

    return (
      <Column className={cn("gap-1.5", isReply && "relative pl-6")}>
        {isReply ? (
          <CornerDownRightIcon className="absolute left-0 top-1 h-4 w-4 text-slate-300" />
        ) : null}
        <Row className="min-w-0 items-center justify-between gap-2">
          <CommentAuthorHeader
            createdAt={comment.createdAt}
            initials={authorInitials}
            name={authorName}
          />
          {renderActionsMenu()}
        </Row>
        <div className="whitespace-pre-line break-normal pl-8 text-sm font-normal leading-snug text-slate-600">
          <RichTextDisplay richText={jsonToHtml(comment.contentJson)} />
        </div>
      </Column>
    );
  };

  const renderReplies = () => {
    if (!hasReplies) return null;

    return (
      <Column className="gap-3 pl-10">
        {replies.map((reply) => (
          <InternalComment comment={reply} isReply key={reply.id} />
        ))}
      </Column>
    );
  };

  const renderReplyActions = () => {
    if (isReply) return null;

    return (
      <Column className={hasReplies ? "pl-10" : "pl-8"}>
        <Button
          className="h-7 w-fit justify-start px-2 text-xs font-medium text-primary-800 hover:text-primary-900"
          onClick={() => {
            onToggleReplying?.(isReplying ? null : comment.id);
          }}
          type="button"
          variant="ghost"
        >
          Reply
        </Button>
        {isReplying ? (
          <ReplyComposer
            candidacyId={comment.candidacyId}
            onSent={() => {
              onToggleReplying?.(null);
            }}
            threadId={comment.id}
          />
        ) : null}
      </Column>
    );
  };

  const render = () => {
    if (isReply) return renderComment();

    return (
      <Column className="gap-3">
        {renderComment()}
        {renderReplies()}
        {renderReplyActions()}
      </Column>
    );
  };

  return render();
};
